import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  readPreferenceCookie,
  writePreferenceCookie,
} from "~/lib/preferenceCookie";
import dayjs from "~/utils/dayjs";

import enAccess from "./locales/en/access.json";
import enAudit from "./locales/en/audit.json";
import enCamera from "./locales/en/camera.json";
import enCommon from "./locales/en/common.json";
import enDataAnalysisOverview from "./locales/en/dataAnalysisOverview.json";
import enManagement from "./locales/en/management.json";
import enProbe from "./locales/en/probe.json";
import enRecordLabelling from "./locales/en/recordLabelling.json";
import ptBRAccess from "./locales/pt-BR/access.json";
import ptBRAudit from "./locales/pt-BR/audit.json";
import ptBRCamera from "./locales/pt-BR/camera.json";
import ptBRCommon from "./locales/pt-BR/common.json";
import ptBRDataAnalysisOverview from "./locales/pt-BR/dataAnalysisOverview.json";
import ptBRManagement from "./locales/pt-BR/management.json";
import ptBRProbe from "./locales/pt-BR/probe.json";
import ptBRRecordLabelling from "./locales/pt-BR/recordLabelling.json";

const LANGUAGE_STORAGE_KEY = "i18n-language";

/**
 * Language is stored in a cookie on the domain authkit-ui and insights-fe
 * share, so a choice made on either surface survives the redirect between
 * them — localStorage is origin-scoped and cannot cross. The localStorage
 * read migrates users who chose a language before the cookie existed.
 */
const getStoredLanguage = (): string => {
  if (typeof window === "undefined") return "en";
  const fromCookie = readPreferenceCookie(LANGUAGE_STORAGE_KEY);
  if (fromCookie) return fromCookie;
  const fromStorage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (fromStorage) {
    writePreferenceCookie(LANGUAGE_STORAGE_KEY, fromStorage);
    return fromStorage;
  }
  return "en";
};

// Keep html.lang and the dayjs global locale in sync with the active language
// (registered before init so it also fires for the boot language). Intl-based
// formatting — notably Highcharts 12 datetime axis labels — follows the page
// language, so pt-BR users get Portuguese month names. Raw dayjs .format()
// calls follow dayjs's GLOBAL locale (Mantine's DatesProvider only covers
// Mantine components); the mapping mirrors App.tsx's datesLocale.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
  dayjs.locale(lng === "pt-BR" ? "pt-br" : "en");
});

i18n.use(initReactI18next).init({
  resources: {
    en: {
      access: enAccess,
      audit: enAudit,
      camera: enCamera,
      common: enCommon,
      dataAnalysisOverview: enDataAnalysisOverview,
      management: enManagement,
      probe: enProbe,
      recordLabelling: enRecordLabelling,
    },
    "pt-BR": {
      access: ptBRAccess,
      audit: ptBRAudit,
      camera: ptBRCamera,
      common: ptBRCommon,
      dataAnalysisOverview: ptBRDataAnalysisOverview,
      management: ptBRManagement,
      probe: ptBRProbe,
      recordLabelling: ptBRRecordLabelling,
    },
  },
  lng: getStoredLanguage(),
  fallbackLng: "en",
  defaultNS: "common",
  interpolation: {
    escapeValue: false,
  },
});

export const changeLanguage = (lng: string) => {
  writePreferenceCookie(LANGUAGE_STORAGE_KEY, lng);
  // Kept in step so a cleared cookie falls back to the same choice.
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  i18n.changeLanguage(lng);
};

export default i18n;
